<script setup lang="ts">
import { useMate } from "@/stores/mate";
import { Mate, MateJournal, Media } from "@/types/entity";
import { useName } from "@/use/common";
import { timeAgo } from "@/modules/common";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useMission } from "@/stores/mission";
import { showConfirmDialog, showLoadingToast } from "vant";
import { sessionStorageSet } from "@/tools/sessionStorage";
import { createOneTimelineEvent } from "@/apis/user";
import { TIMELINE_MODULE_ID as MATE_TIMELINE_MODULE_ID } from "@/stores/module/moduleMate";
import { ref } from "vue";
import { MemoryType } from "@/enums/mateEnums";

const props = defineProps<{
  journal: MateJournal;
  isSelect?: boolean;
  include?: number[];
  isRankingEnabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "select", journal: MateJournal): void;
}>();

const router = useRouter();
const mateStore = useMate();
const { t } = useI18n();
const missionStore = useMission();

const clickLike = async (journal: MateJournal, isLike: boolean) => {
  const { id, userSatisfaction: origin } = journal;
  let current = isLike ? 1 : -1;
  let userSatisfaction = 0;
  if (origin === current) {
    userSatisfaction = 0;
  } else {
    userSatisfaction = current;
  }

  await missionStore.putMateJournal(id, {
    userSatisfaction,
  });
  journal.userSatisfaction = userSatisfaction;
};

const clickDelete = async (journal: MateJournal) => {
  try {
    await showConfirmDialog({
      message: t("Message.deleteJournalConfirm"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
  } catch {
    return;
  }
  const loading = showLoadingToast(t(`Message.loading`));
  await missionStore.deleteMateJournal(journal.id);
  journal.status = 3;

  loading.close();
};

const clickMateWish = (item: MateJournal) => {
  const { content, date, weather, mateId } = item;
  sessionStorageSet("mate-wish-edit", {
    content: `${t(`mission.journey.time`)}: ${date}
${t(`mission.journey.weather`)}: ${weather}

${content}`,
    characterIdzz: [mateId],
  });
  router.push({ name: `mateWishEdit` });
};

const clickTimelineEvent = async (journey: MateJournal) => {
  const { mateId, date: time, content, weather } = journey;
  try {
    await showConfirmDialog({
      title: t("mission.journey.createTimelineConfirmTitle"),
      message: t("mission.journey.createTimelineConfirmDesc"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
  } catch (e) {
    return;
  }

  const loading = showLoadingToast(t(`Message.loading`));

  const text = `${t(`mission.journey.weather`)}: ${weather}

${content}`.replace(/\n/g, "<br />");

  await createOneTimelineEvent({
    entityId: mateId,
    entityType: "mate",
    time,
    content: text,
  });
  loading.close();

  try {
    await showConfirmDialog({
      message: t("mission.journey.afterCreateTimeline"),
      confirmButtonText: t("Buttons.goTo"),
      cancelButtonText: t("Buttons.cancel"),
    });
    router.push({
      name: "mateEdit",
      params: { uuid: mateStore.getMyMateById(mateId)?.uuid },
      hash: "#" + MATE_TIMELINE_MODULE_ID,
    });
  } catch (e) {
    return;
  }
};

const clickMoment = async (journey: MateJournal) => {
  const { mateId, content, weather, date } = journey!;
  let text = `${t(`mission.journey.time`)}: ${date}
${t(`mission.journey.weather`)}: ${weather}

${content}`;

  await mateStore.changeCurrentMate(mateId);
  sessionStorageSet("import-moment", [
    {
      message: text,
      imgList: [],
      videoList: [],
      sharedMessageList: [],
      visibility: 1,
      mentionMateIdzz: [],
      isAiGenerated: true,
    },
  ]);
  router.push({ name: `createThread` });
};

const onGenerateAudio = async (media?: Media, journal?: MateJournal) => {
  if (!journal) return;
  const loading = showLoadingToast(t(`Message.loading`));
  await missionStore.putMateJournal(journal.id, {
    audioMediaId: media?.id ?? 0,
  });
  loading.close();
  if (media) {
    journal.audioMediaId = media.id;
    journal.audioMedia = media;
  } else {
    journal.audioMediaId = undefined;
    journal.audioMedia = undefined;
  }
};

const clickMateHome = (mate: Mate) => {
  if (!props.isRankingEnabled) return;
  router.push({
    name: "mateHome",
    params: { uuid: mate.uuid },
  });
};

const showActionSheet = ref(false);

const actions = [
  {
    name: t(`mission.journey.createTimeline`),
    callback: () => clickTimelineEvent(props.journal),
  },
  // {
  //   name: t(`mission.journey.createMoment`),
  //   callback: () => clickMoment(props.journal),
  // },
  {
    name: t(`mateWish.name`),
    callback: () => clickMateWish(props.journal),
  },
  {
    name: "删除",
    className: "text-error",
    callback: () => clickDelete(props.journal),
  },
  {
    name: "关闭",
    className: "text-base-content/50",
    callback: () => (showActionSheet.value = false),
  },
];

const clickCreateSceneMemory = (journal: MateJournal) => {
  sessionStorageSet("memory:edit:source", {
    journalzz: [journal],
  });

  router.push({
    name: "mateMemoryEdit",
    query: {
      type: MemoryType.Scene,
      mateId: journal.mateId,
      hasSource: 1,
    },
  });
};
</script>
<template>
  <div class="flex items-center justify-between gap-1">
    <div v-if="isSelect">
      <VanCheckbox
        :model-value="include?.includes(journal.id)"
        @click="emit('select', journal)"
      />
    </div>
    <div
      class="rounded-lg border bg-base-300 p-4 w-full"
      @click="emit('select', journal)"
    >
      <template v-if="journal.status === 3">
        <p>该手账已删除</p>
      </template>
      <template v-else>
        <div class="mb-2 flex gap-2">
          <div class="w-12">
            <UserAvatar
              :mate="mateStore.mateIdMap.get(journal.mateId) || journal.mate"
              @click="
                clickMateHome(
                  mateStore.mateIdMap.get(journal.mateId) || journal.mate!
                )
              "
            />
          </div>
          <div class="flex-1 flex flex-col gap-0.5">
            <p class="line-clamp-1">
              {{
                mateStore.mateIdMap.get(journal.mateId)?.name ||
                journal.mate?.name
              }}
            </p>
            <p>
              {{
                `${$t(`mission.journey.time`)}: ${timeAgo(journal.dtCreate)}`
              }}
            </p>
          </div>
        </div>
        <!-- 日期天气 -->
        <div class="text-sm text-base-content/70 mb-2">
          <p>{{ journal.date }}</p>
          <p>{{ journal.weather }}</p>
        </div>

        <!-- 结识的 OC -->
        <div
          v-if="journal.dispatch?.metCharacterzz?.length"
          class="flex gap-2 items-center"
        >
          <div class="flex flex-col gap-2">
            <p>结识的 OC:</p>
          </div>
          <div
            v-for="item in journal.dispatch?.metCharacterzz"
            :key="item.id"
            class="flex items-center gap-2"
            @click="
              router.push({
                name: 'mateHome',
                params: { uuid: item.uuid },
              })
            "
          >
            <div class="w-12 flex flex-col items-center gap-1">
              <UserAvatar :mate="item" class="w-full" />
              <p class="line-clamp-1 text-xs">
                {{ useName(item.name) }}
              </p>
            </div>
          </div>
        </div>

        <!-- 日志内容 -->
        <p class="whitespace-pre-wrap" v-if="!isSelect">
          {{ journal.content }}
        </p>

        <VanTextEllipsis
          v-else
          rows="3"
          :content="journal.content"
          :expand-text="$t(`Buttons.expand`)"
          :collapse-text="$t(`Buttons.collapse`)"
          @click-action.stop
        />

        <template
          v-if="
            !isSelect &&
            !isRankingEnabled &&
            mateStore.isMyMateById(journal.mateId)
          "
        >
          <div>
            <VoiceGenerate
              v-if="mateStore.getMyMateById(journal.mateId)"
              :mate="mateStore.getMyMateById(journal.mateId)!"
              :text="[journal.date, journal.weather, journal.content].join('.')"
              tone-type="voice"
              :active-media="journal.audioMedia"
              @generate="(media) => onGenerateAudio(media, journal)"
            />
          </div>
          <div class="flex gap-2 justify-between mt-2">
            <div class="flex gap-2">
              <button
                :class="[
                  'btn btn-xs btn-outline',
                  journal.userSatisfaction === 1 && 'btn-primary',
                ]"
                @click="clickLike(journal, true)"
              >
                <i class="iconify mingcute--thumb-up-2-line"></i>
              </button>
              <button
                :class="[
                  'btn btn-xs btn-outline',
                  journal.userSatisfaction === -1 && 'btn-primary',
                ]"
                @click="clickLike(journal, false)"
              >
                <i class="iconify mingcute--thumb-down-2-line"></i>
              </button>
              <button
                class="btn btn-xs btn-outline"
                @click="clickCreateSceneMemory(journal)"
              >
                +记忆
              </button>

              <button
                class="btn btn-xs btn-outline"
                @click="clickMoment(journal)"
              >
                +随记
              </button>
            </div>

            <button
              class="btn btn-xs btn-ghost"
              @click="showActionSheet = true"
            >
              <i class="iconify mingcute--more-1-line"></i>
            </button>
          </div>
          <!-- 按钮组 -->
          <!-- <div class="flex gap-2 mt-4">
            <button
              class="btn btn-xs btn-outline"
              @click="clickTimelineEvent(journal)"
            >
              {{ $t(`mission.journey.createTimeline`) }}
            </button>
            <button
              class="btn btn-xs btn-outline"
              @click="clickMoment(journal)"
            >
              {{ $t(`mission.journey.createMoment`) }}
            </button>
            <button
              class="btn btn-xs btn-outline"
              @click="clickMateWish(journal)"
            >
              {{ $t(`mateWish.name`) }}
            </button>
          </div> -->
        </template>
      </template>
    </div>

    <VanActionSheet
      v-model:show="showActionSheet"
      :actions="actions"
      close-on-click-action
    />
  </div>
</template>
